/* External dependencies */
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import * as AWSCognito from 'amazon-cognito-identity-js';
import update from 'immutability-helper';

/* Local dependencies */
import { setupClient } from '../../../../clients/client';
import { AuthAction, AuthActionTypes, AuthViewMode } from './types';

export interface AuthState {
  client?: ApolloClient<NormalizedCacheObject>;
  currentUser?: any;
  initializing: boolean;
  loading: boolean;
  error?: any;
  username?: string;
  viewMode: AuthViewMode;
  awaitingConfirmation?: boolean;
  confirmationSuccess?: boolean;
  confirmationCode?: string;
}

export const initialAuthState: AuthState = {
  initializing: true,
  loading: false,
  viewMode: 'login',
};

export default function authReducer(state = initialAuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AuthActionTypes.INIT_CLIENT_SUCCEEDED:
      const { session } = action;

      const idTokenObj = new AWSCognito.CognitoIdToken({
        IdToken: session.idToken?.jwtToken ?? session.idToken,
      });
      const jwtPayload = idTokenObj.decodePayload();
      const currentUser = jwtPayload;
      const client = setupClient(session);

      return {
        ...state,
        client: client,
        currentUser: currentUser,
        initializing: false,
        loading: false,
      };

    case AuthActionTypes.INIT_CLIENT_FAILED:
      return update(state, {
        initializing: { $set: false },
      });

    case AuthActionTypes.INIT_ANONYMOUS_CLIENT:
      return update(state, {
        client: { $set: action.client },
      });

    case AuthActionTypes.LOGOUT_REQUEST:
      return update(state, {
        $unset: ['error'],
        loading: { $set: true },
      });

    case AuthActionTypes.LOGOUT_SUCCESS:
      return update(state, {
        loading: { $set: false },
        $unset: ['client', 'currentUser'],
      });

    case AuthActionTypes.LOGOUT_ERROR:
      const { error } = action;

      return update(state, {
        error: { $set: error },
        loading: { $set: false },
      });

    case AuthActionTypes.SEND_VERIFICATION_CODE_REQUEST:
      return update(state, {
        loading: { $set: true },
      });

    case AuthActionTypes.SET_USER_SESSION:
      return update(state, {
        $unset: ['error'],
        loading: { $set: false },
      });

    case AuthActionTypes.CONFIRM_REGISTRATION_REQUEST:
      return update(state, {
        $unset: ['error'],
        loading: { $set: true },
      });

    case AuthActionTypes.CONFIRM_REGISTRATION_SUCCESS:
      return update(state, {
        loading: { $set: false },
        confirmationSuccess: { $set: true },
        awaitingConfirmation: { $set: false },
        viewMode: { $set: 'login' },
      });

    case AuthActionTypes.CONFIRM_REGISTRATION_ERROR:
      return update(state, {
        error: { $set: action.error },
        loading: { $set: false },
        confirmationSuccess: { $set: false },
      });

    case AuthActionTypes.SET_AUTH_VIEW_MODE:
      return update(state, {
        viewMode: { $set: action.mode },
        $unset: ['error'],
      });

    case AuthActionTypes.SET_CONFIRMATION_CODE:
      return update(state, {
        confirmationCode: { $set: action.code },
      });

    case AuthActionTypes.SEND_AUTH_ERROR:
      return update(state, {
        error: { $set: action.error },
      });

    case AuthActionTypes.RESET_AUTH_STATE:
      return initialAuthState;

    default:
      return state;
  }
}
